import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import styles from '../styles/pages/createProjectStyles';
import desktopStyles from '../styles/pages/createProjectDesktopStyles';
import { useMediaQuery } from 'react-responsive';

interface ProjectFormData {
  name: string;
  identifier: string;
  description: string;
  manager: string;
  startDate: string;
  endDate: string;
}

export default function CreateProject() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    identifier: '',
    description: '',
    manager: '',
    startDate: '',
    endDate: ''
  });
  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateIdentifier = (name: string) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      identifier: generateIdentifier(value)
    }));
  };

  const handleSubmit = async () => {
    setError(null);
    
    // 유효성 검사
    if (!formData.name) {
      setError('프로젝트 이름을 입력해주세요.');
      return;
    }

    setLoading(true);
    
    // 자동으로 필요한 필드 채우기
    const today = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 1);
    
    const submitData = {
      name: formData.name,
      identifier: generateIdentifier(formData.name),
      description: '',
      manager: 'Admin',
      startDate: today.toISOString().split('T')[0],
      endDate: nextYear.toISOString().split('T')[0]
    };
    try {
      const response = await fetch(`${API_URL}/api/redmine/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '프로젝트 생성에 실패했습니다.');
      }

      const result = await response.json();
      
      if (Platform.OS === 'web') {
        alert('프로젝트가 성공적으로 생성되었습니다!');
      } else {
        Alert.alert('성공', '프로젝트가 성공적으로 생성되었습니다!');
      }
      
      navigate('/projectstatus');
    } catch (err) {
      setError(err instanceof Error ? err.message : '프로젝트 생성 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const currentStyles = isDesktop ? { ...styles, ...desktopStyles } : styles;
  return (
    <ScrollView style={currentStyles.container}>
      <View style={currentStyles.formWrapper}>
        <View style={currentStyles.formHeader}>
          <Text style={currentStyles.formTitle}>새 프로젝트 생성</Text>
          <Text style={currentStyles.formSubtitle}>프로젝트 이름을 입력하세요.</Text>
        </View>

        <View style={currentStyles.formContent}>
          {error && (
            <View style={[currentStyles.errorContainer, { marginTop: 0, marginBottom: 20 }]}>
              <Text style={currentStyles.errorText}>{error}</Text>
            </View>
          )}
          
          <View style={currentStyles.formGroup}>
            <Text style={currentStyles.label}>프로젝트 이름 *</Text>
            <TextInput
              style={currentStyles.input}
              value={formData.name}
              onChangeText={handleNameChange}
              placeholder="프로젝트 이름을 입력하세요"
              placeholderTextColor="#999"
              maxLength={100}
            />
          </View>

          <View style={currentStyles.actions}>
            <TouchableOpacity
              style={[currentStyles.button, currentStyles.cancelButton]}
              onPress={() => navigate(-1)}
              disabled={loading}
            >
              <Text style={currentStyles.cancelButtonText}>취소</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[currentStyles.button, currentStyles.submitButton]}
              onPress={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={currentStyles.submitButtonText}>프로젝트 생성</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}